import React, { useEffect, useState } from 'react';

interface ChatPanelProps {
  isOpen: boolean;
  title: string;
  initialMessage?: string;
  onClose: () => void;
  onSend: (message: string, history: { role: string; content: string }[]) => Promise<string> | string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ isOpen, title, initialMessage, onClose, onSend }) => {
  const [messages, setMessages] = useState<{ role: 'system' | 'user' | 'assistant'; content: string }[]>(
    initialMessage ? [{ role: 'system', content: initialMessage }] : []
  );
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Reflect changes to initialMessage after mount (e.g., when greeting arrives)
  useEffect(() => {
    if (initialMessage) {
      setMessages((prev) => {
        // Avoid duplicating the same initial message
        if (prev.length === 0 || prev[0].content !== initialMessage) {
          return [{ role: 'system', content: initialMessage }, ...prev.filter((m) => m.role !== 'system')];
        }
        return prev;
      });
    }
  }, [initialMessage]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    const nextHistory = [...messages, { role: 'user' as const, content: userMsg }];
    setMessages(nextHistory);
    setInput('');
    setSending(true);
    try {
      const reply = await onSend(userMsg, nextHistory.map(m => ({ role: m.role, content: m.content })));
      setMessages(prev => [...prev, { role: 'assistant', content: String(reply || '') }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative z-10 ml-auto h-full w-full max-w-xl bg-white dark:bg-gray-900 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          {messages.map((m, idx) => (
            <div key={idx} className={`p-3 rounded-lg max-w-[85%] ${m.role === 'user' ? 'ml-auto bg-blue-100 dark:bg-blue-900 text-gray-900 dark:text-gray-100' : m.role === 'assistant' ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100' : 'bg-yellow-50 dark:bg-yellow-900 text-gray-900 dark:text-gray-100'}`}>
              {m.content}
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex items-center space-x-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
            className="flex-1 input"
            placeholder="Ask a question about the contract(s)..."
            disabled={sending}
          />
          <button onClick={handleSend} disabled={sending} className="btn-primary">Send</button>
        </div>
      </div>
    </div>
  );
};


